import { lazy } from 'react'
import type React from 'react'

/* ============================================================
   Component Registry — Visual Builder Core
   Maps (section_key, variant_key) → React component + metadata.
   Every section type has at least one 'default' variant that
   matches the pre-builder look.
   ============================================================ */

/**
 * Section variant components accept heterogeneous props (content, tenantName, services, etc.)
 * built dynamically at render time. We use `Record<string, unknown>` as the opaque props type
 * for the registry, and a `lazyVariant` helper hides the single required cast so that
 * individual variant entries stay clean and `any`-free.
 */
type SectionProps = Record<string, unknown>

/** Wraps React.lazy so that components with specific props can be stored in the generic registry. */
function lazyVariant(
  factory: () => Promise<{ default: React.ComponentType<never> }>
): React.LazyExoticComponent<React.ComponentType<SectionProps>> {
  const typedFactory = factory as unknown as () => Promise<{
    default: React.ComponentType<SectionProps>
  }>
  return lazy(typedFactory)
}

export interface VariantDef {
  key: string
  label: string
  description: string
  /** CSS gradient string used as thumbnail preview in the drawer */
  thumbnail: string
  /** Emoji icon for quick identification */
  icon: string
  component: React.LazyExoticComponent<React.ComponentType<SectionProps>>
}

export interface SectionCategory {
  key: string
  label: string
  icon: string
  description: string
  variants: VariantDef[]
}

// ── Hero Variants ─────────────────────────────────────────────

const HERO_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Classic Center',
    description: 'Centered headline with gradient orbs',
    thumbnail: 'linear-gradient(135deg, #0a0a1a 0%, #1a1a3e 50%, #0d0d2b 100%)',
    icon: '🎯',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroClassic')),
  },
  {
    key: 'split',
    label: 'Split Image',
    description: 'Left text, right full-bleed image',
    thumbnail: 'linear-gradient(90deg, #0a0a1a 50%, #2a1a3e 50%)',
    icon: '◧',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroSplit')),
  },
  {
    key: 'video',
    label: 'Cinematic',
    description: 'Animated gradient background with overlay',
    thumbnail: 'linear-gradient(135deg, #1a0a2e 0%, #0a1a3e 40%, #2e0a1a 100%)',
    icon: '🎬',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroVideo')),
  },
  {
    key: 'minimal',
    label: 'Minimal Typography',
    description: 'Just massive text, zero decoration',
    thumbnail: 'linear-gradient(180deg, #0a0a0f 0%, #111 100%)',
    icon: '✦',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroMinimal')),
  },
  {
    key: 'wave',
    label: 'Wave',
    description: 'Bottom wave clip-path with floating shapes',
    thumbnail: 'linear-gradient(180deg, #0a1628 0%, #162a4a 60%, #0a1628 100%)',
    icon: '🌊',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroWave')),
  },
  {
    key: 'neon',
    label: 'Neon Parallax',
    description: 'Parallax background with animated text reveal',
    thumbnail: 'linear-gradient(135deg, #0a0a1a 0%, #2e0a3e 50%, #0a1a2e 100%)',
    icon: '⚡',
    component: lazyVariant(() => import('@/components/tenant/variants/hero/HeroNeon')),
  },
]

// ── About Variants ────────────────────────────────────────────

const ABOUT_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Story Block',
    description: 'Image and text side by side',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    icon: '📖',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutStory')),
  },
  {
    key: 'timeline',
    label: 'Timeline',
    description: 'Vertical timeline of milestones',
    thumbnail: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 50%, #0a0a1a 100%)',
    icon: '📅',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutTimeline')),
  },
  {
    key: 'stats',
    label: 'Stats Spotlight',
    description: 'Big animated numbers with descriptions',
    thumbnail: 'linear-gradient(135deg, #0a1a0a 0%, #1a2e1a 100%)',
    icon: '📊',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutStats')),
  },
  {
    key: 'vision',
    label: 'Vision',
    description: 'Split layout philosophy with gradient blobs',
    thumbnail: 'linear-gradient(135deg, #1a0a2e 0%, #2e1a3e 100%)',
    icon: '👁️',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutVision')),
  },
  {
    key: 'approach',
    label: 'Approach',
    description: 'Three-step timeline with animated dots',
    thumbnail: 'linear-gradient(180deg, #0a0a1a 0%, #2e1a2e 50%, #0a0a1a 100%)',
    icon: '🎯',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutApproach')),
  },
  {
    key: 'founder',
    label: 'Founder Story',
    description: 'Split narrative with quote and portrait',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a0f2e 100%)',
    icon: '✍️',
    component: lazyVariant(() => import('@/components/tenant/variants/about/AboutFounder')),
  },
]

// ── Services Variants ─────────────────────────────────────────

const SERVICES_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Grid Cards',
    description: 'Card grid with prices and durations',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    icon: '✂️',
    component: lazyVariant(() => import('@/components/tenant/variants/services/ServicesGrid')),
  },
  {
    key: 'list',
    label: 'Stacked List',
    description: 'Minimalist list with hover reveals',
    thumbnail: 'linear-gradient(180deg, #0a0a12 0%, #12121e 100%)',
    icon: '☰',
    component: lazyVariant(() => import('@/components/tenant/variants/services/ServicesList')),
  },
  {
    key: 'showcase',
    label: 'Showcase',
    description: 'Large cards with visual emphasis',
    thumbnail: 'linear-gradient(135deg, #1a0f2e 0%, #2e1a3e 100%)',
    icon: '🎭',
    component: lazyVariant(() => import('@/components/tenant/variants/services/ServicesShowcase')),
  },
]

// ── Team Variants ─────────────────────────────────────────────

const TEAM_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Photo Cards',
    description: 'Photo cards with hover bio reveal',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    icon: '👥',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamCards')),
  },
  {
    key: 'minimal',
    label: 'Minimal List',
    description: 'Compact horizontal scroll layout',
    thumbnail: 'linear-gradient(90deg, #0a0a12 0%, #1a1a2e 100%)',
    icon: '👤',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamMinimal')),
  },
  {
    key: 'flip-cards',
    label: '3D Flip Cards',
    description: 'Glassmorphic cards with 3D flip to reveal bio',
    thumbnail: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1a1a 100%)',
    icon: '🔄',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamFlipCards')),
  },
  {
    key: 'signature-neon',
    label: 'Signature Neon',
    description: 'Bold neon glow cards with gradient CTAs',
    thumbnail: 'linear-gradient(135deg, #1a0020 0%, #ff007a33 50%, #1a0020 100%)',
    icon: '✨',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamSignatureNeon')),
  },
  {
    key: 'immersive-glass',
    label: 'Immersive Glass',
    description: 'Premium glassmorphism cards with integrated stats',
    thumbnail: 'linear-gradient(135deg, #1a0a2e 0%, #2e1a3e 50%, #1a1a2e 100%)',
    icon: '💎',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamImmersiveGlass')),
  },
  {
    key: 'garden',
    label: 'Garden',
    description: 'Botanical organic cards with emerald tones',
    thumbnail: 'linear-gradient(135deg, #0a1a0a 0%, #1a2e1a 100%)',
    icon: '🌿',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamGarden')),
  },
  {
    key: 'high-contrast',
    label: 'High Contrast',
    description: 'Black & white editorial with grayscale images',
    thumbnail: 'linear-gradient(180deg, #000 0%, #333 100%)',
    icon: '◐',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamHighContrast')),
  },
  {
    key: 'matrix',
    label: 'Matrix',
    description: 'Cyberpunk aesthetic with scan-lines and cyan neon',
    thumbnail: 'linear-gradient(135deg, #001a1a 0%, #003333 50%, #001a1a 100%)',
    icon: '💻',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamMatrix')),
  },
  {
    key: 'booking-selector',
    label: 'Booking Selector',
    description: 'Circular tile grid for stylist selection in booking flow',
    thumbnail: 'linear-gradient(135deg, #0a0a12 0%, #1a1200 50%, #0a0a12 100%)',
    icon: '🎯',
    component: lazyVariant(
      () => import('@/components/tenant/variants/team/StylistBookingSelector')
    ),
  },
  {
    key: 'liquid-glass',
    label: 'Liquid Glass',
    description: 'iOS 26 inspired liquid glass with dynamic blur and interactive borders',
    thumbnail: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    icon: '🧊',
    component: lazyVariant(() => import('@/components/tenant/variants/team/TeamLiquidGlass')),
  },
]

// ── Testimonials Variants ─────────────────────────────────────

const TESTIMONIALS_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Grid',
    description: 'Card grid of testimonials',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    icon: '⭐',
    component: lazyVariant(
      () => import('@/components/tenant/variants/testimonials/TestimonialsGrid')
    ),
  },
  {
    key: 'featured',
    label: 'Featured Quote',
    description: 'One large quote with supporting cards',
    thumbnail: 'linear-gradient(135deg, #1a0a2e 0%, #0a1a2e 100%)',
    icon: '💬',
    component: lazyVariant(
      () => import('@/components/tenant/variants/testimonials/TestimonialsFeatured')
    ),
  },
  {
    key: 'carousel',
    label: 'Carousel',
    description: 'Single rotating testimonial spotlight',
    thumbnail: 'linear-gradient(180deg, #0a0a1a 0%, #1a1a3e 100%)',
    icon: '🔄',
    component: lazyVariant(
      () => import('@/components/tenant/variants/testimonials/TestimonialsCarousel')
    ),
  },
]

// ── Gallery Variants ──────────────────────────────────────────

const GALLERY_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Masonry Grid',
    description: 'Grid with lightbox on click',
    thumbnail: 'linear-gradient(135deg, #0f0f1a 0%, #1a1a2e 100%)',
    icon: '🖼️',
    component: lazyVariant(() => import('@/components/tenant/variants/gallery/GalleryMasonry')),
  },
  {
    key: 'fullbleed',
    label: 'Full Bleed',
    description: 'Edge-to-edge images with parallax',
    thumbnail: 'linear-gradient(180deg, #0a0a1a 0%, #2e2e3e 50%, #0a0a1a 100%)',
    icon: '🖥️',
    component: lazyVariant(() => import('@/components/tenant/variants/gallery/GalleryFullbleed')),
  },
  {
    key: 'neon-masonry',
    label: 'Neon Masonry',
    description: 'CSS masonry with neon ring borders and hover effects',
    thumbnail: 'linear-gradient(135deg, #0a0a1a 0%, #1a0a2e 50%, #0a1a1a 100%)',
    icon: '✨',
    component: lazyVariant(() => import('@/components/tenant/variants/gallery/GalleryNeonMasonry')),
  },
]

// ── CTA Variants ──────────────────────────────────────────────

const CTA_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Split CTA',
    description: 'Left copy, right CTA button',
    thumbnail: 'linear-gradient(90deg, #0a0a1a 40%, #2e1a3e 100%)',
    icon: '📣',
    component: lazyVariant(() => import('@/components/tenant/variants/cta/CtaSplit')),
  },
  {
    key: 'banner',
    label: 'Full-Width Banner',
    description: 'Bold gradient with centered CTA',
    thumbnail: 'linear-gradient(135deg, #1a0a3e 0%, #3e1a2e 50%, #1a2e3e 100%)',
    icon: '🎯',
    component: lazyVariant(() => import('@/components/tenant/variants/cta/CtaBanner')),
  },
  {
    key: 'liquid-glass',
    label: 'Liquid Glass CTA',
    description: 'Deep blur backdrop with floating reactive orbs and glassmorph button',
    thumbnail: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    icon: '💧',
    component: lazyVariant(() => import('@/components/tenant/variants/cta/CtaLiquidGlass')),
  },
]

// ── Contact Variants ──────────────────────────────────────────

const CONTACT_VARIANTS: VariantDef[] = [
  {
    key: 'default',
    label: 'Neon Contact',
    description: 'Grid layout with gradient headline and lucide icons',
    thumbnail: 'linear-gradient(135deg, #1a0a2e 0%, #0a1a3e 100%)',
    icon: '📍',
    component: lazyVariant(() => import('@/components/tenant/variants/contact/ContactNeon')),
  },
]

// ── Master Registry ───────────────────────────────────────────

export const COMPONENT_REGISTRY: SectionCategory[] = [
  {
    key: 'hero',
    label: 'Hero',
    icon: '🎯',
    description: 'Main banner with headline and CTA',
    variants: HERO_VARIANTS,
  },
  {
    key: 'about',
    label: 'About',
    icon: '📖',
    description: 'About your business',
    variants: ABOUT_VARIANTS,
  },
  {
    key: 'services',
    label: 'Services',
    icon: '✂️',
    description: 'Service menu and pricing',
    variants: SERVICES_VARIANTS,
  },
  {
    key: 'team',
    label: 'Team',
    icon: '👥',
    description: 'Your stylists and staff',
    variants: TEAM_VARIANTS,
  },
  {
    key: 'testimonials',
    label: 'Testimonials',
    icon: '⭐',
    description: 'Client reviews and feedback',
    variants: TESTIMONIALS_VARIANTS,
  },
  {
    key: 'gallery',
    label: 'Gallery',
    icon: '🖼️',
    description: 'Photo gallery showcase',
    variants: GALLERY_VARIANTS,
  },
  {
    key: 'cta',
    label: 'Call to Action',
    icon: '📣',
    description: 'Conversion-focused banner',
    variants: CTA_VARIANTS,
  },
  {
    key: 'contact',
    label: 'Contact',
    icon: '📍',
    description: 'Contact information and location',
    variants: CONTACT_VARIANTS,
  },
]

// ── Lookup Helpers ────────────────────────────────────────────

/** Get a specific variant's component for a section */
export function getVariantComponent(
  sectionKey: string,
  variantKey: string = 'default'
): React.LazyExoticComponent<React.ComponentType<SectionProps>> | null {
  const category = COMPONENT_REGISTRY.find((c) => c.key === sectionKey)
  if (!category) return null
  const variant = category.variants.find((v) => v.key === variantKey) ?? category.variants[0]
  return variant?.component ?? null
}

/** Get all variants for a section type */
export function getSectionVariants(sectionKey: string): VariantDef[] {
  const category = COMPONENT_REGISTRY.find((c) => c.key === sectionKey)
  return category?.variants ?? []
}

/** Get variant metadata */
export function getVariantDef(sectionKey: string, variantKey: string): VariantDef | undefined {
  const category = COMPONENT_REGISTRY.find((c) => c.key === sectionKey)
  return category?.variants.find((v) => v.key === variantKey)
}

/** Extract variant_key from section config JSON */
export function getVariantKeyFromConfig(config: unknown): string {
  if (config && typeof config === 'object' && 'variant_key' in config) {
    return String((config as Record<string, unknown>).variant_key)
  }
  return 'default'
}
